import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Download, Sparkles, Eye, CheckCircle2, Undo2, Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { API_BASE } from '../../config/api';
import { downloadStudyGuide, periodLabel } from '../../lib/studyGuide';
import StudyGuideReviewDialog from './StudyGuideReviewDialog';

const API = `${API_BASE}/api`;

const STATUS = {
  generating: { label: 'Generating…', cls: 'bg-sky-500/15 text-sky-300' },
  draft: { label: 'Awaiting approval', cls: 'bg-amber-500/15 text-amber-300' },
  approved: { label: 'Approved · visible to learners', cls: 'bg-green-500/15 text-green-400' },
  failed: { label: 'Failed', cls: 'bg-red-500/15 text-red-300' },
};

function GuideRow({ row, unit, busy, onGenerate, onReview, onApprove, onRevoke, onDownload }) {
  const g = row.guide;
  const status = g ? STATUS[g.status] : null;
  const ready = g && (g.status === 'draft' || g.status === 'approved');
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3 p-4 rounded-lg border border-zinc-800 bg-zinc-900/60" data-testid={`study-guide-row-${row.period_id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium truncate">{periodLabel(unit, row.position, row.title)}</p>
        <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-zinc-500">
          <span>{row.course_count} course{row.course_count === 1 ? '' : 's'}</span>
          {status && <Badge className={status.cls} data-testid={`study-guide-status-${row.period_id}`}>{status.label}</Badge>}
          {g?.generated_at && <span>Generated {new Date(g.generated_at).toLocaleDateString()}</span>}
          {g?.status === 'failed' && <span className="text-red-300 truncate max-w-xs" title={g.error}>{g.error}</span>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {g?.status === 'generating' ? (
          <Button size="sm" disabled className="bg-zinc-800 text-zinc-300"><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Generating</Button>
        ) : (
          <Button size="sm" variant="outline" disabled={busy || row.course_count === 0} onClick={() => onGenerate(row)}
            className="border-zinc-700 text-zinc-200" title={row.course_count === 0 ? 'Add a course to this period first' : ''}
            data-testid={`study-guide-generate-${row.period_id}`}>
            {g ? <RefreshCw className="h-4 w-4 mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            {g ? 'Regenerate' : 'Generate'}
          </Button>
        )}
        {ready && (
          <>
            <Button size="sm" variant="outline" onClick={() => onReview(row)} className="border-zinc-700 text-zinc-200" data-testid={`study-guide-review-${row.period_id}`}>
              <Eye className="h-4 w-4 mr-1.5" />Review
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDownload(row)} className="border-zinc-700 text-zinc-200" data-testid={`study-guide-download-${row.period_id}`}>
              <Download className="h-4 w-4 mr-1.5" />.docx
            </Button>
            {g.status === 'draft' ? (
              <Button size="sm" disabled={busy} onClick={() => onApprove(row)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`study-guide-approve-${row.period_id}`}>
                <CheckCircle2 className="h-4 w-4 mr-1.5" />Approve
              </Button>
            ) : (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => onRevoke(row)} className="border-zinc-700 text-zinc-300" data-testid={`study-guide-revoke-${row.period_id}`}>
                <Undo2 className="h-4 w-4 mr-1.5" />Unpublish
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function StudyGuidesTab({ programmeId, token, unit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}/study-guides`, { headers });
      setRows(res.data.periods || []);
    } catch (e) {
      toast.error('Failed to load study guides');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId, token]);

  useEffect(() => { load(); }, [load]);

  const anyGenerating = rows.some((r) => r.guide?.status === 'generating');
  useEffect(() => {
    if (!anyGenerating) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [anyGenerating, load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const base = (row) => `${API}/programmes/${programmeId}/periods/${row.period_id}/study-guide`;
  const onGenerate = (row) => act(() => axios.post(`${base(row)}/generate`, {}, { headers }), 'Generating study guide — this takes about a minute');
  const onApprove = (row) => act(() => axios.post(`${base(row)}/approve`, {}, { headers }), 'Study guide approved — learners can now download it');
  const onRevoke = (row) => act(() => axios.post(`${base(row)}/revoke`, {}, { headers }), 'Study guide hidden from learners');
  const onDownload = (row) => downloadStudyGuide(programmeId, row.period_id, token).catch(() => toast.error('Download failed'));

  return (
    <Card className="bg-zinc-900 border-zinc-800" data-testid="study-guides-tab">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2"><FileText className="h-5 w-5 text-teal-400" />Study Guides</CardTitle>
        <p className="text-sm text-zinc-400">
          One Word study guide per {unit.toLowerCase()}, written by NAVI from the courses in that {unit.toLowerCase()}. Review it, then approve to let enrolled learners download it.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-zinc-500 text-sm">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-zinc-500 text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Save the programme with at least one {unit.toLowerCase()} first.</p>
        ) : rows.map((row) => (
          <GuideRow key={row.period_id} row={row} unit={unit} busy={busy}
            onGenerate={onGenerate} onReview={setReview} onApprove={onApprove} onRevoke={onRevoke} onDownload={onDownload} />
        ))}
      </CardContent>
      <StudyGuideReviewDialog
        open={!!review}
        onOpenChange={(o) => !o && setReview(null)}
        programmeId={programmeId}
        row={review}
        unit={unit}
        token={token}
      />
    </Card>
  );
}
